import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ModalController, Platform } from '@ionic/angular';
import { of } from 'rxjs';
import { map, mergeMap, takeWhile } from 'rxjs/operators';
import { MSRemoteLoggerCategory } from 'src/app/adapters/remote-logger/utils/remote-logger.utils';
import { LocationRequiredPage } from 'src/app/pages/setup/location-required/location-required.page';
import { observableAsPromise } from 'src/app/pages/utils/promise.utils';
import { environment } from 'src/environments/environment';
import { RequestExactAlarmModalPage } from '../../pages/setup/exact-alarm/request-exact-alarm.modal.page';
import { ExactAlarmPermissionUseCase } from '../../use-case/exact-alarm-permission/exact-alarm-permission.use-case';
import { RemoteLoggerUseCase } from '../../use-case/remote-logger/remote-logger.use-case';
import { UserStorageUseCase } from '../../use-case/user-storage/user-storage.use-case';
import { PlatformEnum } from '../../utils/platform.utils';
import { ApiServiceOld } from '../api-old.service';
import { BadgeService } from '../badge.service';
import { CONSTANTS } from '../config.service';
import { DailySurveyService } from '../daily-survey.service';
import { DateTimeService } from '../date-time/date-time.service';
import { GeowatcherService } from '../geowatcher.service';
import { GisService } from '../gis.service';
import { LoggerService } from '../logger.service';
import { PersonsService } from '../persons.service';
import { PlatformService } from '../platform/platform.service';
import { PosterService } from '../poster.service';
import { PrefsService } from '../prefs.service';
import { ProxySurveyService } from '../proxy-survey.service';
import { SenderSurveyService } from '../sender-survey.service';
import { StorageService } from '../storage.service';
import { StudyService } from '../study.service';
import { LoadService } from '../survey/load.service';
import { PrepopulateService } from '../survey/prepopulate.service';
import { TimeLoggerService } from '../survey/time-logger.service';
import { TripSurveyInitializerService } from '../trip-survey-initializer.service';
import { TripSurveyService } from '../trip-survey.service';

@Injectable({
  providedIn: 'root'
})
export class PostSignupInitializerService {
  isLongDistance;
  baseConfig;

  // eslint-disable-next-line max-lines-per-function
  constructor(
    public http: HttpClient,
    public platform: Platform,
    private modalCtrl: ModalController,
    private logger: LoggerService,
    private dateTime: DateTimeService,
    private gis: GisService,
    private prefs: PrefsService,
    private study: StudyService,
    private geowatcher: GeowatcherService,
    private persons: PersonsService,
    private poster: PosterService,
    private load: LoadService,
    private tripSurvey: TripSurveyService,
    private dailySurvey: DailySurveyService,
    private proxySurvey: ProxySurveyService,
    private storage: StorageService,
    private badge: BadgeService,
    private senderSurvey: SenderSurveyService,
    private tripSurveyInitializer: TripSurveyInitializerService,
    private prepop: PrepopulateService,
    private timeLogger: TimeLoggerService,
    private userStorage: UserStorageUseCase,
    private api: ApiServiceOld,
    private exactAlarmPermissionUseCase: ExactAlarmPermissionUseCase,
    private platformService: PlatformService,
    private remoteLogger: RemoteLoggerUseCase,
    private userStorageUseCase: UserStorageUseCase
  ) {
    this.logger.log('Initializer constructor', MSRemoteLoggerCategory.POST_SIGNUP_INITIALIZER_SERVICE);
  }

  // eslint-disable-next-line max-lines-per-function
  async configureSettings() {
    const self = this;
    //Config is not yet used
    return this.prefs
      .getPromise('studyType')
      .then((studyType) => {
        // 1 for short surveys, 2 for long
        self.isLongDistance = studyType === 2;
        self.baseConfig = environment.distanceConfig;
        if (self.baseConfig.autoConfirmCount < 2) {
          self.baseConfig.autoConfirmCount = 2; // LD prepop would fail otherwise
        }
        CONSTANTS.tripRecording.TRIP_METERS = self.baseConfig.ld_tripDistance;
        CONSTANTS.tripRecording.NEAR_METERS = self.baseConfig.nearMeters;
        CONSTANTS.tripRecording.DWELL_METERS = self.baseConfig.dwellMeters;
        CONSTANTS.tripRecording.GEOFENCE_RADIUS = self.baseConfig.stationaryRadius;
        CONSTANTS.tripRecording.DWELL_SECONDS = self.baseConfig.dwellSeconds;
        CONSTANTS.surveyAutoConfirmCount = self.baseConfig.autoConfirmCount;
        return self.prefs.getStorePromise(CONSTANTS.longDistanceModeKey, self.isLongDistance);
      })
      .catch((err) => {
        this.logger.log(err, MSRemoteLoggerCategory.POST_SIGNUP_INITIALIZER_SERVICE);
      });
  }

  // eslint-disable-next-line max-lines-per-function
  async initCrashReporting() {

    const authCode = await this.prefs.getPromiseOfTypeOrDefault<string>('authCode', null);
    const studyId = await this.prefs.getPromiseOfTypeOrDefault<string>('studyId', null);
    const activationCode = await this.prefs.getPromiseOfTypeOrDefault<string>('activationCode', null);
    const studyAddress = await observableAsPromise(this.userStorageUseCase.getServerAddress())

    this.remoteLogger.setTag('user.id', authCode);
    this.remoteLogger.setTag('user.studyId', studyId);
    this.remoteLogger.setTag('user.activationCode', activationCode);

    this.remoteLogger.setUserProperty({
      id: authCode
    });

    this.remoteLogger.setGlobalAttributes({
      'user.activation_code': activationCode,
      'study.id': studyId,
      'study.address': studyAddress,
    });

    if (!CONSTANTS.IS_DEVICE) {
      return Promise.resolve();
    }

    if (!activationCode) {
      return Promise.resolve(null);
    }

    return Promise.resolve(null);
  }

  uploadData() {
    this.logger.log('uploadData', MSRemoteLoggerCategory.POST_SIGNUP_INITIALIZER_SERVICE);
    // TODO hide this check behind the sender services, like all other services for different platforms
    if (this.platform.is('android') && CONSTANTS.IS_DEVICE) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      this.senderSurvey.queueAll().then(() => {
        this.geowatcher.uploadData();
      });
    } else {
      if (CONSTANTS.IS_DEVICE) {
        this.geowatcher.uploadData();
      }
      this.senderSurvey.queueThenSend();
    }
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    this.poster.send();
  }

  async presentModalLocationRequired() {
    const data = {};
    if (!CONSTANTS.IS_DEVICE) {
      return Promise.resolve();
    }

    return this.prefs.getPromiseOrDefault('didLocationPrompt').then(async (res) => {
      if (res === true) {
        return Promise.resolve();
      }

      const modal = await this.modalCtrl.create({ component: LocationRequiredPage, componentProps: data, cssClass: 'modal-topmost' });
      await modal.present();
      return modal.onDidDismiss().then(() => this.prefs.getStorePromise('didLocationPrompt', true));
    });
  }

  async presentModalScheduleExactAlarmRequiredIfNeeded() {
    const isPermissionNeeded = await of(this.platformService.getPlatform()).pipe(
      takeWhile((platform) => platform === PlatformEnum.ANDROID),
      mergeMap((_) => this.exactAlarmPermissionUseCase.hasPermission()),
      map((hasPermission) => !hasPermission)
    ).toPromise();

    if (!isPermissionNeeded) { return; }

    const modal = await this.modalCtrl.create({
      component: RequestExactAlarmModalPage,
      cssClass: 'modal-topmost'
    });

    await modal.present();
    await modal.onDidDismiss();
  }

  // see TODO above about belonging here...
  hasUnsentData() {
    // wanted to keep geowatcher (backend) out of trip/daily survey services as they're front-end only
    //   so calling here instead
    const promises = !CONSTANTS.IS_DEVICE
      ? [this.senderSurvey.hasUnsent()]
      : this.platform.is('android')
        ? [this.geowatcher.hasUnsentData()]
        : [this.geowatcher.hasUnsentData(), this.senderSurvey.hasUnsent()];

    promises.push(this.tripSurvey.hasUnqueued(), this.dailySurvey.hasUnqueued(), this.proxySurvey.hasUnqueued());
    // TODO check poster data?
    return Promise.all(promises).then((results) => results.some((res) => res === true));
  }

  // eslint-disable-next-line max-lines-per-function
  initialize() {
    return (
      this.configureSettings()
        .then(() => {
          this.logger.log('Configured settings', MSRemoteLoggerCategory.POST_SIGNUP_INITIALIZER_SERVICE);
          return this.initCrashReporting();
        })
        .then(() => {
          this.logger.log('Initialized crash reporting', MSRemoteLoggerCategory.POST_SIGNUP_INITIALIZER_SERVICE);
          return Promise.all([this.study.initialize(), this.gis.initialize(this.isLongDistance) /*, persons.putDataInRootScope()*/]);
        })
        .then(() => {
          this.logger.log('Initialized study and gis', MSRemoteLoggerCategory.POST_SIGNUP_INITIALIZER_SERVICE);
          return Promise.all([this.poster.initialize()]);
        })
        .then(() => {
          this.logger.log('Initialized poster', MSRemoteLoggerCategory.POST_SIGNUP_INITIALIZER_SERVICE);
          return this.timeLogger.initialize();
        })
        .then(() => {
          this.logger.log('Initialized timeLogger', MSRemoteLoggerCategory.POST_SIGNUP_INITIALIZER_SERVICE);
          return this.persons.initialize();
        })
        .then(() => {
          this.logger.log('Initialized persons', MSRemoteLoggerCategory.POST_SIGNUP_INITIALIZER_SERVICE);
          return this.storage.initialize();
        })
        .then(() => {
          this.logger.log('Initialized storage', MSRemoteLoggerCategory.POST_SIGNUP_INITIALIZER_SERVICE);
          return this.load.initialize();
        }) // wrapped for thisArg context; surveys need load, load needs persons
        .then(() => {
          this.logger.makeSuccessLogger('Initialized survey recipes');
          return this.tripSurvey.initialize(this.isLongDistance);
        })
        .then(() => {
          this.logger.log('Initialized trip survey', MSRemoteLoggerCategory.POST_SIGNUP_INITIALIZER_SERVICE);
          return Promise.all([this.proxySurvey.initialize(this.isLongDistance), this.dailySurvey.initialize(this.isLongDistance)]);
        }) // need gis and poster, needed for geoWatcher (table creation)
        .then(() => {
          this.logger.log('Initialized proxy and daily surveys', MSRemoteLoggerCategory.POST_SIGNUP_INITIALIZER_SERVICE);
          return this.prepop.initialize();
        }) // needs tripSurvey
        .then(async () => {
          this.logger.log('Initialized prepop', MSRemoteLoggerCategory.POST_SIGNUP_INITIALIZER_SERVICE);

          await this.presentModalScheduleExactAlarmRequiredIfNeeded();
          await this.presentModalLocationRequired();

          await this.geowatcher.initialize(this.baseConfig);
        }) // creates backend tables, needed below (badge might try to use immediately)
        .then(() => {
          // return this.presentModalLocationRequired()
          //     .then(() => {
          this.logger.log('Initialized geowatcher', MSRemoteLoggerCategory.POST_SIGNUP_INITIALIZER_SERVICE);
          return Promise.all([
            this.tripSurveyInitializer.initialize(this.isLongDistance),
            this.senderSurvey.initialize(),
            this.badge.initialize()
          ]);
          // })
        })
        .then(() => {
          this.logger.log('Initialized trip survey intializer, sender survey, and badge', MSRemoteLoggerCategory.POST_SIGNUP_INITIALIZER_SERVICE);
        })
        //.then(() => { return this.transitStops.initialize(); })
        //.then(() => { this.logger.log('Initialized transitStops'); })
        .then(() => {
          this.logger.log('initializer promises complete', MSRemoteLoggerCategory.POST_SIGNUP_INITIALIZER_SERVICE);
          // eslint-disable-next-line @typescript-eslint/no-floating-promises
          this.poster.send(); // get init msgs/errors up to the server
        })
        .catch((err) => {
          if (err === 'Study expired') {
            this.logger.warn(err, MSRemoteLoggerCategory.POST_SIGNUP_INITIALIZER_SERVICE);
            return this.prefs.getStorePromise('abortMessageKey', 'ABORT_MESSAGES.STUDY_EXPIRED').then(() => Promise.reject(err));
          } else {
            this.logger.error(err, MSRemoteLoggerCategory.POST_SIGNUP_INITIALIZER_SERVICE);
            throw err;
          }
        })
    );
  }
}